import { useMutation } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FormProvider,
  type Path,
  useFieldArray,
  useForm,
} from 'react-hook-form';

import { type CreatePlatformInviteRequest, inviteCreatePlatformInvite } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@column/ui';

import { FormField, NotificationList } from '~/components';
import { RenderFields } from '~/components/RenderFields';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { precannedRolesByID } from '~/repositories/RoleRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ApiError } from '~/typings/API';
import { validateEmail } from '~/util';

export interface RoleOption {
  label: string;
  value: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  roles: RoleOption[];
}

interface InviteEntry {
  email: string;
  roleId: string;
}

interface FormValues {
  inviteEntries: InviteEntry[];
}

const MAX_INVITES = 5;

const newEntry = (): InviteEntry => ({ roleId: '', email: '' });

const emailRules = {
  validate: (email: unknown) => !email || validateEmail(String(email)) || 'Enter a valid email address',
};

const EMAIL_LABEL_ID = 'invite-users-email-label';
const ROW_TRANSITION = { duration: 0.15, ease: 'easeOut' } as const;

// Grows a row open when it's added and collapses it before it's removed. Overflow is only clipped while the
// height animates, so focus rings and shadows aren't cut off once the row settles.
const InviteRowPresence: React.FC<
  PropsWithChildren<{ isRemoving: boolean; onExpanded: (element: HTMLElement) => void; onCollapsed: () => void }>
> = ({ isRemoving, onExpanded, onCollapsed, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  // The exit animation completes too; only the first collapse should remove the row from the form.
  const hasCollapsed = useRef(false);
  const [isAnimating, setIsAnimating] = useState(false);

  return (
    <motion.div
      ref={ref}
      initial={{ height: 0, opacity: 0 }}
      animate={isRemoving ? { height: 0, opacity: 0 } : { height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={ROW_TRANSITION}
      onAnimationStart={() => setIsAnimating(true)}
      onAnimationComplete={() => {
        setIsAnimating(false);
        if (isRemoving) {
          if (!hasCollapsed.current) onCollapsed();
          hasCollapsed.current = true;
        } else if (ref.current) onExpanded(ref.current);
      }}
      aria-hidden={isRemoving || undefined}
      className={cn(
        (isAnimating || isRemoving) && 'overflow-hidden',
        isRemoving && 'pointer-events-none',
        '[&:last-child>div]:pb-0'
      )}
    >
      {/* Row spacing lives inside the animated height so nothing jumps as rows come and go. */}
      <div className="pb-4">{children}</div>
    </motion.div>
  );
};

interface InviteUsersFormProps {
  roles: RoleOption[];
  isPending: boolean;
  onClose: () => void;
  onSubmit: (entries: InviteEntry[]) => void;
}

// Rendered inside DialogContent, which unmounts once the dialog finishes closing, so every open starts with a fresh
// form instead of resetting (and briefly re-rendering) the previous one.
const InviteUsersForm: React.FC<InviteUsersFormProps> = ({ roles, isPending, onClose, onSubmit: submitEntries }) => {
  const { addDangerNotification, clearNotifications } = useNotificationStore();
  const { handleSubmitErrors } = useFormErrorHandler({ defaultDisplayType: 'popup' });

  const formMethods = useForm<FormValues>({ defaultValues: { inviteEntries: [newEntry()] } });
  const { fields, append, remove } = useFieldArray({ control: formMethods.control, name: 'inviteEntries' });
  // Rows stay in the form until their collapse finishes, so they keep their field names while animating out.
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const rowCount = fields.length - removingIds.size;
  const inviteLabel = rowCount > 1 ? 'Invite Users' : 'Invite User';

  useEffect(() => {
    clearNotifications('popup');
  }, [clearNotifications]);

  const onSubmit = formMethods.handleSubmit(({ inviteEntries }) => {
    const entries = inviteEntries.filter(
      (entry, index) => entry.email.length > 0 && !removingIds.has(fields[index]?.id)
    );
    if (entries.length === 0) {
      addDangerNotification({ content: 'Enter a valid email address', display: 'popup' });
      return;
    }
    submitEntries(entries);
  }, handleSubmitErrors);

  const startRemoving = (id: string) => setRemovingIds((ids) => new Set(ids).add(id));

  const finishRemoving = (id: string) => {
    const index = fields.findIndex((field) => field.id === id);
    if (index === -1) return;
    remove(index);
    setRemovingIds((ids) => {
      const next = new Set(ids);
      next.delete(id);
      return next;
    });
  };

  const renderRow =
    (index: number) =>
    ({ name, value, onChange, onBlur }: ControllerRenderProps<FormValues>, { error }: ControllerFieldState) => (
      <div className="flex items-center gap-2">
        <Input
          type="email"
          name={name}
          placeholder="name@company.com"
          aria-labelledby={EMAIL_LABEL_ID}
          aria-invalid={!!error}
          disabled={isPending}
          value={String(value ?? '')}
          onChange={onChange}
          onBlur={onBlur}
          className="min-w-0 flex-1"
        />
        <Controller
          control={formMethods.control}
          name={`inviteEntries.${index}.roleId`}
          rules={{
            validate: (roleId, values) =>
              !values.inviteEntries[index]?.email || !!roleId || 'Select a role for each invite',
          }}
          render={({ field: roleField, fieldState }) => (
            <Select value={roleField.value} onValueChange={roleField.onChange} disabled={isPending}>
              <SelectTrigger
                size="lg"
                className="w-[200px] shrink-0"
                aria-label="Role"
                aria-invalid={!!fieldState.error}
              >
                <SelectValue placeholder="Select Role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        <AnimatePresence initial={false}>
          {rowCount > 1 && (
            // Width (and the -8px margin cancelling the flex gap) animate so the fields slide over to make room.
            // The button fades and scales instead of being clipped, so its focus ring is never cut off.
            <motion.div
              key="remove"
              initial={{ width: 0, marginLeft: -8, opacity: 0, scale: 0.6 }}
              animate={{ width: 32, marginLeft: 0, opacity: 1, scale: 1 }}
              exit={{ width: 0, marginLeft: -8, opacity: 0, scale: 0.6 }}
              transition={ROW_TRANSITION}
              className="flex shrink-0 justify-center"
            >
              <button
                type="button"
                aria-label={`Remove invite ${index + 1}`}
                disabled={isPending}
                onClick={() => startRemoving(fields[index].id)}
                className="flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground disabled:pointer-events-none disabled:opacity-50 dark:hover:bg-white/10"
              >
                <Icon.Close className="[--icon-size-x:16px] [--icon-size-y:16px]" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );

  return (
    <>
      <DialogHeader className="-mx-6 -mt-2 flex-row items-start justify-between gap-4 border-b border-gray-100 px-6 pb-4 dark:border-white/[0.08]">
        <DialogTitle>{inviteLabel}</DialogTitle>
        <DialogClose asChild>
          {/* 24px hit area trimmed to the title's 20px line height so the icon centers on the text. */}
          <button
            type="button"
            aria-label="Close"
            disabled={isPending}
            className="-my-0.5 -mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground disabled:pointer-events-none disabled:opacity-50 dark:hover:bg-white/10"
          >
            <Icon.Close className="[--icon-size-x:16px] [--icon-size-y:16px]" />
          </button>
        </DialogClose>
      </DialogHeader>

      {/* Inside the dialog so errors stack above its overlay. */}
      <NotificationList display="popup" spacing="bottom" className="!m-0" />

      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={onSubmit} className="pb-6">
          {RenderFields<FormValues>({
            sections: [
              {
                variant: 'isCompact',
                fields: [
                  {
                    // Rows live in one block (rather than as separate section fields) so they can animate in and
                    // out without the section's grid gap snapping ahead of them.
                    content: (
                      <div>
                        <div id={EMAIL_LABEL_ID} className="mb-2 text-sm leading-[18px] font-medium text-foreground">
                          Email Address
                        </div>
                        <AnimatePresence initial={false}>
                          {fields.map((field, index) => (
                            <InviteRowPresence
                              key={field.id}
                              isRemoving={removingIds.has(field.id)}
                              onCollapsed={() => finishRemoving(field.id)}
                              // @column/ui Input doesn't forward refs, so focus the new email field through the DOM.
                              onExpanded={(element) => element.querySelector<HTMLInputElement>('input')?.focus()}
                            >
                              <FormField<FormValues>
                                id={`inviteEntries.${index}.email` as Path<FormValues>}
                                noStyle
                                rules={emailRules}
                              >
                                {renderRow(index)}
                              </FormField>
                            </InviteRowPresence>
                          ))}
                        </AnimatePresence>
                        {rowCount < MAX_INVITES && (
                          <Button
                            type="button"
                            size="sm"
                            variant="muted"
                            className="w-full bg-gray-50 hover:bg-gray-100 dark:bg-white/5 dark:hover:bg-white/10"
                            disabled={isPending}
                            onClick={() => append(newEntry(), { shouldFocus: false })}
                          >
                            <Icon.Plus className="[--icon-size:16px]" />
                            Add Invite
                          </Button>
                        )}
                      </div>
                    ),
                  },
                ],
              },
            ],
          })}
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
      </FormProvider>

      <DialogFooter className="sm:justify-between">
        <Button variant="secondary" size="sm" type="button" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button size="sm" type="button" onClick={onSubmit} disabled={isPending}>
          <Icon.Plus className="[--icon-size-x:16px] [--icon-size-y:16px]" />
          {isPending ? 'Inviting…' : inviteLabel}
        </Button>
      </DialogFooter>
    </>
  );
};

export const InviteUsersModal: React.FC<Props> = ({ onClose, onSave, open, roles }) => {
  const { currentPlatform } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();

  const inviteMutation = useMutation({
    mutationFn: (entries: InviteEntry[]) => {
      const platformId = currentPlatform?.id;
      if (!platformId) {
        throw new Error('No Platform ID provided, please reload and try again');
      }

      return Promise.all(
        entries.map((entry) => {
          const platformRole = precannedRolesByID[entry.roleId]?.name;
          const body: CreatePlatformInviteRequest = {
            platform_id: platformId,
            email: entry.email,
            platform_role: platformRole,
            role_id: platformRole === undefined ? entry.roleId : undefined,
          };

          return inviteCreatePlatformInvite({ path: { platform_id: platformId }, body, throwOnError: true });
        })
      );
    },
    onSuccess: (results, entries) => {
      onSave();
      const noun = entries.length === 1 ? 'Invite' : 'Invites';
      const emails = entries.map((entry) => entry.email).join(', ');
      addSuccessNotification({
        content: results.some((result) => result.response.status === 202)
          ? `${noun} for "${emails}" submitted for approval.`
          : `${noun} sent to "${emails}".`,
      });
    },
    onError: (cause) => {
      const error = cause as ApiError;
      const limitMessage = (error?.details as { limit?: string })?.limit;
      addDangerNotification({
        content:
          error?.code === 'limit_exceeded' && limitMessage ? limitMessage : error?.message || 'An error occurred',
        display: 'popup',
      });
    },
  });

  const isPending = inviteMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isPending && onClose()}>
      <DialogContent
        overlayScroll
        showCloseButton={false}
        aria-describedby={undefined}
        className="gap-3 sm:max-w-[544px]"
        onOpenAutoFocus={(event) => {
          // The header close button comes first in the DOM; start on the field instead.
          event.preventDefault();
          (event.currentTarget as HTMLElement).querySelector('input')?.focus();
        }}
      >
        <InviteUsersForm
          roles={roles}
          isPending={isPending}
          onClose={onClose}
          onSubmit={(entries) => inviteMutation.mutate(entries)}
        />
      </DialogContent>
    </Dialog>
  );
};
